import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Loader2, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";

// This page handles two flows:
// 1. PayFast success return: /payment/success/:pid  — auto-confirms + refreshes
// 2. Mock dev checkout:      /payment/process/:pid  — shows "Pay Now (Mock)" button
export default function PaymentProcess({ successMode = false }) {
  const { pid } = useParams();
  const nav = useNavigate();
  const { refresh } = useAuth();
  const [done, setDone] = useState(false);
  const [loading, setLoading] = useState(successMode); // auto-complete if coming from PayFast

  const complete = async () => {
    setLoading(true);
    try {
      await api.post(`/payments/${pid}/complete`);
      await refresh();
      setDone(true);
      toast.success("Payment successful! Plan upgraded.");
      setTimeout(() => nav("/dashboard"), 2000);
    } catch (err) {
      toast.error("Payment failed — please contact support");
      nav("/pricing");
    } finally {
      setLoading(false);
    }
  };

  // If coming from PayFast success return, auto-complete on mount
  useEffect(() => {
    if (successMode) {
      complete();
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <div className="min-h-screen flex items-center justify-center px-6 grid-bg">
      <div className="w-full max-w-md glass-strong rounded-3xl p-10 text-center animate-fade-up">
        {done ? (
          <>
            <CheckCircle2 size={48} className="mx-auto text-emerald-400"/>
            <h1 className="mt-4 text-2xl font-bold" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>Payment successful!</h1>
            <p className="text-sm text-zinc-400 mt-2">Your plan has been upgraded. Redirecting to dashboard…</p>
            <Loader2 size={18} className="mx-auto mt-4 animate-spin text-indigo-400"/>
          </>
        ) : loading ? (
          <>
            <Loader2 size={40} className="mx-auto text-indigo-400 animate-spin"/>
            <h1 className="mt-4 text-xl font-bold">Confirming payment…</h1>
            <p className="text-sm text-zinc-400 mt-2">Please wait while we confirm your payment.</p>
          </>
        ) : (
          <>
            <div className="mb-6 text-xs uppercase tracking-widest text-zinc-400">Dev / Mock Checkout</div>
            <h1 className="text-2xl font-bold" style={{fontFamily:"'Cabinet Grotesk', sans-serif"}}>Complete your payment</h1>
            <p className="text-sm text-zinc-400 mt-2">Click below to simulate a successful PayFast transaction.</p>
            <Button
              onClick={complete}
              className="mt-6 w-full bg-indigo-600 hover:bg-indigo-700 glow-primary h-11"
              data-testid="payment-complete-btn"
            >
              Pay Now (Mock)
            </Button>
            <Button onClick={() => nav("/pricing")} variant="ghost" className="mt-2 w-full">Cancel</Button>
          </>
        )}
      </div>
    </div>
  );
}
